import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Table from 'src/components/Table/Table';
import { useDebounce, useLocalStorage } from 'usehooks-ts';
import { DataTableV3PrimaryAction, DataTableV3Props, DataTableV3SecondaryAction } from '../types';
import { createExpanderColumm, createSelectionColumn } from '../utils';
import DataTableColumnEditor from './DataTableColumnEditor';
import DataTableExcelExportButton from './DataTableExcelExportButton';
import DataTableGlobalFilter from './DataTableGlobalFilter';
import DataTablePagination from './DataTablePagination';
import DataTableRowsSelectedText from './DataTableRowsSelectedText';
import PrimaryActions from './PrimaryActions';
import { DataTableV3LocalStoragePrefixes } from '../constants/localStorage';
import '../../../styles/style.scss';
import '../style.scss';
import { Button, Icon, Skeleton, Tooltip } from '@innovationnorway/cl-react';

const defaultPageSizeOptions = [5, 10, 25, 50, 100];

export const DataTableV3 = <TData extends object>({
  id,

  data,
  columns,
  actions,
  state: defaultState,
  initialState,
  onGlobalFilterChange,
  pageCount,

  disableSelection = false,
  disablePagination = false,
  hideScrollbar = false,
  disableColumnEditor = false,
  disableGlobalFilter = false,
  disableExport = false,
  autoResetPageIndex = true,

  primaryActionLocation = 'top',

  pageSizeOptions = defaultPageSizeOptions,
  hideFilterText = false,
  filtersActive,
  pageSize = undefined,
  overridePaginationTotalNumber = undefined,
  paginationListeners = undefined,
  loading,

  enableRowSelection,
  enableMultiRowSelection = true,
  disabledRowTitle,
  showRowsSelectedText = false,
  rowsSelectedSingularTranslation = undefined,
  rowsSelectedPluralTranslation = undefined,
  setSelectedRows,
  texts = undefined,
  placeholderText = undefined,

  preselectedRows = {},

  customColumnFilters = undefined,
  columnFilters: defaultColumnFilters,
  sorting: defaultSortingState,

  getRowCanExpand,
  renderSubComponent,
  globalFilterFn,
  getRowId,
}: DataTableV3Props<TData>) => {
  const { t: tTableTitles } = useTranslation('tableTitles');
  const { t: tCommon } = useTranslation();

  const [globalFilter, setGlobalFilter] = useState<string>(initialState?.globalFilter ?? '');
  const [sortingState, setSortingState] = useLocalStorage<SortingState>(
    `${DataTableV3LocalStoragePrefixes.sortingState}${id}`,
    defaultSortingState ?? [],
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(defaultColumnFilters ?? []);

  const resolvedColumnFilters = useMemo(() => {
    if (defaultColumnFilters) {
      return defaultColumnFilters;
    }

    return columnFilters;
  }, [columnFilters, defaultColumnFilters]);

  const debouncedGlobalFilter = useDebounce(globalFilter, 150);

  const [rowSelection, setRowSelection] = useState(preselectedRows);
  const [columnVisibility, setColumnVisibility] = useLocalStorage<Record<string, boolean>>(
    `${DataTableV3LocalStoragePrefixes.columnVisibility}${id}`,
    columns.reduce(
      (acc, column) => {
        if (column.id) {
          acc[column.id] = column.meta?.hide === true ? false : true;
        }

        return acc;
      },
      {} as Record<string, boolean>,
    ),
  );
  const [storagePageSize, setStoragePageSize] = useLocalStorage<number>(
    `${DataTableV3LocalStoragePrefixes.pageSize}${id}`,
    pageSize ? pageSize : pageSizeOptions[0],
  );

  const selectionColumn = createSelectionColumn<TData>({
    disabledRowTitle,
    enableMultiRowSelection,
  });

  const expanderColumn = createExpanderColumm<TData>();

  const tableData = React.useMemo(() => (loading ? Array(5).fill({}) : data), [loading, data]);

  const alteredColumns = React.useMemo(() => {
    const cols: ColumnDef<TData, any>[] = columns.map((column) => {
      return {
        ...column,
        sortingFn: column.sortingFn ?? 'text',
      };
    });

    if (getRowCanExpand) {
      cols.unshift(expanderColumn);
    }

    if (!disableSelection) {
      cols.unshift(selectionColumn);
    }

    return loading
      ? cols.map((column) => ({
          ...column,
          cell: () => <Skeleton width="100%" height="1.25rem" />,
        }))
      : cols;
  }, [columns, getRowCanExpand, disableSelection, loading, expanderColumn, selectionColumn]);

  const table = useReactTable<TData>({
    data: tableData,
    columns: alteredColumns,
    initialState: {
      pagination: disablePagination
        ? { pageSize: Number.MAX_SAFE_INTEGER }
        : {
            pageSize: storagePageSize,
          },
      rowSelection: rowSelection,
    },
    state: {
      ...defaultState,
      rowSelection: rowSelection,
      columnVisibility: disableColumnEditor ? undefined : columnVisibility,
      sorting: sortingState,
      globalFilter: debouncedGlobalFilter,
      columnFilters: resolvedColumnFilters,
    },
    pageCount: pageCount,
    autoResetPageIndex: autoResetPageIndex,

    enableRowSelection: enableRowSelection,
    enableMultiRowSelection: enableMultiRowSelection,

    onSortingChange: setSortingState,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn: globalFilterFn ?? 'auto',

    getRowId,
    getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
  });

  useEffect(() => {
    if (setSelectedRows) {
      const rows = table.getSelectedRowModel().flatRows.map((row) => row.original);
      setSelectedRows(rows);
    }
  }, [rowSelection, setSelectedRows, table]);

  const tableRowsLength = table.getRowModel().rows.length;

  //Potensielle sideeffekter? Tror ikke det, men lar denne kommentaren stå for synlighet
  useEffect(() => {
    table.resetRowSelection();
  }, [table, columns, tableRowsLength]);

  async function handleActionOnClick(
    action: DataTableV3PrimaryAction<TData> | DataTableV3SecondaryAction<TData>,
  ) {
    const rows = table.getSelectedRowModel().flatRows.map((row) => row.original);

    const promiseOrResult = action.onClick?.(rows);
    if (promiseOrResult instanceof Promise) {
      const resetSelected = await promiseOrResult;
      if (resetSelected === true) {
        table.toggleAllRowsSelected(false);
      }
    } else {
      if (promiseOrResult === true) {
        table.toggleAllRowsSelected(false);
      }
    }
  }

  const getSortState = (sortState: string | false) => {
    if (sortState === 'asc') return 'ascending';
    else if (sortState === 'desc') return 'descending';
    else if (sortState === false) return 'none';
    return 'none';
  };

  return (
    <>
      {actions?.primary && (
        <div className="display--flex align-items--flex-start justify-content--flex-start gap--2 margin-bottom--2">
          {primaryActionLocation === 'top' && <PrimaryActions actions={actions} table={table} />}
        </div>
      )}

      <DataTableGlobalFilter
        value={globalFilter}
        onChange={(e) => {
          table.toggleAllRowsSelected(false);
          setGlobalFilter(e.currentTarget.value);
          table.setPageIndex(0);
          if (onGlobalFilterChange) onGlobalFilterChange(e);
        }}
        disabled={disableGlobalFilter}
        texts={texts}
      />

      {customColumnFilters && <div className="margin-bottom--4">{customColumnFilters}</div>}
      <div className="display--flex align-items--center justify-content--space-between">
        <div className="display--flex align-items--center">
          {showRowsSelectedText && (
            <div className="selected-rows-text-container">
              <DataTableRowsSelectedText
                table={table}
                showRowsSelectedText={showRowsSelectedText}
                singularTranslation={rowsSelectedSingularTranslation}
                pluralTranslation={rowsSelectedPluralTranslation}
              />
            </div>
          )}
          {actions?.secondary && (
            <div className="display--flex align-items--center gap--2 secondary-actions-container">
              {actions?.secondary?.map((action, index) => (
                <Button
                  key={index}
                  type="button"
                  variant="tertiary"
                  disabled={action.disabled}
                  onClick={() => handleActionOnClick(action)}
                >
                  {action.icon ? <Icon {...action.icon} /> : null}
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        <div className="display--flex align-items--center gap--2">
          <Tooltip placement="top" content={tCommon('editTableView.tooltip')}>
            {/* DataTableColumnEditor doesn't forward refs, so the tooltip anchors to this span */}
            <span>
              <DataTableColumnEditor
                table={table}
                disabled={disableColumnEditor}
                disableSelection={disableSelection}
              />
            </span>
          </Tooltip>

          {!disableExport && <DataTableExcelExportButton table={table} />}
        </div>
      </div>
      {!hideFilterText && (
        <p className={filtersActive ? 'visibility--visible' : 'visibility--hidden'}>
          {table.getFilteredRowModel().flatRows.length === 1 ? (
            tCommon('oneResult')
          ) : (
            <Trans
              t={tCommon}
              i18nKey="numberOfResults"
              values={{ resultsCount: table.getFilteredRowModel().flatRows.length }}
            />
          )}
        </p>
      )}

      <div
        className={classNames('display--block margin-bottom--2', {
          'overflow-x--scroll': !hideScrollbar,
          'data-table-v3__scroll-container': hideScrollbar,
        })}
      >
        <Table
          className={classNames({
            'table--selectable': !disableSelection,
          })}
        >
          <Table.Head>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Row key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <Table.Header
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`${
                        !header.column.getCanSort()
                          ? ''
                          : header.column.getIsSorted()
                            ? 'active-sort-column'
                            : 'column-sort-button'
                      }`}
                      sort={
                        !header.column.getCanSort() ? undefined : getSortState(header.column.getIsSorted())
                      }
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder ? null : header.column.columnDef.id?.startsWith('__') ? (
                        <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                      ) : (
                        <span>
                          {tTableTitles(
                            (typeof header.column.columnDef.header === 'string'
                              ? header.column.columnDef.header
                              : (header.column.columnDef.id ?? '')) as any,
                            {
                              defaultValue: flexRender(header.column.columnDef.header, header.getContext()),
                            },
                          )}
                        </span>
                      )}
                    </Table.Header>
                  );
                })}
              </Table.Row>
            ))}
          </Table.Head>
          <Table.Body>
            {table.getRowModel().rows.length > 0 || loading ? (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <Table.Row
                    key={`${row.id}-row`}
                    className={classNames({
                      selected: row.getIsSelected(),
                    })}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell
                        key={cell.id}
                        className="opportunity-table__cell"
                        style={{
                          wordWrap: 'break-word',
                          width: cell.column.columnDef.size,
                          minWidth: cell.column.columnDef.minSize,
                          maxWidth: cell.column.columnDef.maxSize,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                  {row.getIsExpanded() && (
                    <Table.Row key={`${row.id}-expanded`}>
                      <Table.Cell colSpan={table.getAllColumns().length} className="opportunity-table__cell">
                        {renderSubComponent ? renderSubComponent({ row }) : null}
                      </Table.Cell>
                    </Table.Row>
                  )}
                </React.Fragment>
              ))
            ) : (
              <Table.Row>
                <Table.Cell colSpan={table.getAllColumns().length} className="opportunity-table__cell">
                  {placeholderText ? placeholderText : tCommon('noResults')}
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>

      <DataTablePagination
        table={table}
        disabled={disablePagination}
        pageSizeOptions={pageSizeOptions}
        paginationListeners={paginationListeners}
        overridePaginationTotalNumber={overridePaginationTotalNumber}
        setStoragePageSize={setStoragePageSize}
      />

      {primaryActionLocation === 'bottom' && <PrimaryActions actions={actions} table={table} />}
    </>
  );
};
